import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { BookOpen, Calendar, Users, Clock, ArrowRight, ClipboardCheck, FileCheck, ShieldCheck } from 'lucide-react';
import { AttendanceModal } from '../modals/AttendanceModal';
import { EvaluationsModal } from '../modals/EvaluationsModal';
import { ProtocoloBimModal } from '../ProtocoloBimModal';
import { EmptyState } from '../ui/EmptyState';
import { CardSkeleton } from '../ui/Skeleton';

interface Course {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  thumbnail_url?: string;
  profiles?: {
    full_name: string;
  };
}

interface ModuleProgress {
  courseId: string;
  completed: number;
  total: number;
  percentage: number;
}

export function CoursesList() {
  const { profile } = useAuth();
  const { setSelectedCourse } = useNavigation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [evaluationsModalOpen, setEvaluationsModalOpen] = useState(false);
  const [selectedCourseForAttendance, setSelectedCourseForAttendance] = useState<{id: string, name: string} | null>(null);
  const [modulesProgress, setModulesProgress] = useState<Record<string, ModuleProgress>>({});
  const [protocoloCourseId, setProtocoloCourseId] = useState<string | null>(null);

  useEffect(() => {
    loadCourses();
    // Dep en profile?.id (string), no en profile (objeto) — evita recargar
    // la lista de cursos cada vez que Supabase refresca el token al volver
    // de otra ventana.
  }, [profile?.id, profile?.role]);

  useEffect(() => {
    if (courses.length > 0) {
      loadModulesProgress();
    }
  }, [courses]);

  const loadCourses = async () => {
    try {
      if (profile?.role === 'docente') {
        const { data, error } = await supabase
          .from('courses')
          .select('*')
          .or(`docente_id.eq.${profile.id},secondary_docente_id.eq.${profile.id}`)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setCourses(data || []);
      } else if (profile?.role === 'alumno') {
        const { data, error } = await supabase
          .from('course_enrollments')
          .select(`
            courses!inner (
              id,
              name,
              description,
              start_date,
              end_date,
              status,
              thumbnail_url,
              profiles:docente_id (full_name)
            )
          `)
          .eq('alumno_id', profile.id)
          .eq('status', 'active')
          .eq('courses.status', 'active');

        if (error) throw error;
        setCourses(data?.map((enrollment: any) => enrollment.courses).filter(Boolean) || []);
      }
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadModulesProgress = async () => {
    try {
      const progressMap: Record<string, ModuleProgress> = {};

      for (const course of courses) {
        const { data: modules, error } = await supabase
          .from('modules')
          .select('id, end_date, status')
          .eq('course_id', course.id);

        if (error) throw error;

        const today = new Date();
        const totalModules = modules?.length || 0;
        const completedModules = modules?.filter((module) => {
          if (!module.end_date) return false;
          const endDate = new Date(module.end_date);
          return today > endDate;
        }).length || 0;

        const percentage = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

        progressMap[course.id] = {
          courseId: course.id,
          completed: completedModules,
          total: totalModules,
          percentage
        };
      }

      setModulesProgress(progressMap);
    } catch (error) {
      console.error('Error loading modules progress:', error);
    }
  };

  const handleCourseClick = (course: Course) => {
    setSelectedCourse({ id: course.id, name: course.name });
  };

  const handleAttendanceClick = (e: React.MouseEvent, course: Course) => {
    e.stopPropagation();
    setSelectedCourseForAttendance({ id: course.id, name: course.name });
    setAttendanceModalOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div>
          <h1 className="text-display text-3xl font-bold text-white mb-2">Mis Cursos</h1>
          <p className="text-muted">Cargando…</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <div>
        <h1 className="text-display text-3xl font-bold text-white mb-2">Mis Cursos</h1>
        <p className="text-muted">
          {profile?.role === 'docente'
            ? 'Cursos que impartes'
            : 'Cursos en los que estás inscrito'}
        </p>
      </div>

      {courses.length === 0 ? (
        <div className="glass-surface rounded-xl border border-white/[0.06]">
          <EmptyState
            icon={BookOpen}
            title="No hay cursos disponibles"
            description={profile?.role === 'docente'
              ? 'No tienes cursos asignados en este momento'
              : 'No estás inscrito en ningún curso actualmente'}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <div
              key={course.id}
              className="glass-surface rounded-xl overflow-hidden border border-white/[0.06] hover:border-cubus-lime/40 transition-all group"
            >
              <div className="aspect-square relative">
                {course.thumbnail_url ? (
                  <img
                    src={course.thumbnail_url}
                    alt={course.name}
                    className="w-full h-full object-cover bg-gray-800 rounded-lg"
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-br from-cubus-lime/20 to-purple-500/20"></div>
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-50"></div>
                  </>
                )}
                <div className="absolute top-4 right-4 flex flex-col items-end gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      course.status === 'active'
                        ? 'bg-green-500/20 text-green-400 border border-green-500'
                        : 'bg-gray-500/20 text-muted border border-gray-500'
                    }`}
                  >
                    {course.status === 'active' ? 'Activo' : 'Completado'}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setProtocoloCourseId(course.id); }}
                    title="Protocolo BIM CUBUS"
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-cubus-lime/20 text-cubus-lime border border-cubus-lime/40 hover:bg-cubus-lime hover:text-cubus-bg transition-all"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span>Protocolo BIM</span>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cubus-lime transition-colors">
                  {course.name}
                </h3>
                <p className="text-muted text-sm mb-4 line-clamp-2">{course.description}</p>

                {modulesProgress[course.id] && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-muted">Progreso</span>
                      <span className="text-sm font-bold text-cubus-lime">{modulesProgress[course.id].percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full bg-cubus-lime rounded-full transition-all duration-500"
                        style={{ width: `${modulesProgress[course.id].percentage}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  {profile?.role === 'alumno' && course.profiles && (
                    <div className="flex items-center gap-2 text-sm text-muted">
                      <Users className="w-4 h-4" />
                      <span>Docente: {course.profiles.full_name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {new Date(course.start_date + 'T00:00:00').toLocaleDateString('es-CL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })} -{' '}
                      {new Date(course.end_date + 'T00:00:00').toLocaleDateString('es-CL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted">
                    <Clock className="w-4 h-4" />
                    <span>
                      {(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const endDate = new Date(course.end_date + 'T00:00:00');
                        const daysRemaining = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                        return daysRemaining > 0 ? `${daysRemaining} días restantes` : 'Curso finalizado';
                      })()}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <button
                    onClick={() => handleCourseClick(course)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cubus-lime text-cubus-bg rounded-lg hover:bg-cubus-lime-dim transition-all font-semibold group-hover:shadow-lg group-hover:shadow-cubus-lime/20"
                  >
                    <span>Ver Módulos</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  {profile?.role === 'docente' && (
                    <>
                      <button
                        onClick={(e) => handleAttendanceClick(e, course)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-cubus-lime/10 text-cubus-lime rounded-lg hover:bg-cubus-lime/20 transition-all font-medium border border-cubus-lime/30 hover:border-cubus-lime/40"
                      >
                        <ClipboardCheck className="w-5 h-5" />
                        <span>Asistencia Alumnos</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCourseForAttendance({ id: course.id, name: course.name });
                          setEvaluationsModalOpen(true);
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-cubus-lime/10 text-cubus-lime rounded-lg hover:bg-cubus-lime/20 transition-all font-medium border border-cubus-lime/30 hover:border-cubus-lime/40"
                      >
                        <FileCheck className="w-5 h-5" />
                        <span>Evaluaciones</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {profile?.role === 'docente' && courses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="glass-surface rounded-xl p-6 border border-white/[0.06]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-muted">Total Cursos</h3>
              <BookOpen className="w-5 h-5 text-cubus-lime" />
            </div>
            <p className="text-3xl font-bold text-white">{courses.length}</p>
          </div>

          <div className="glass-surface rounded-xl p-6 border border-white/[0.06]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-muted">Cursos Activos</h3>
              <BookOpen className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              {courses.filter((c) => c.status === 'active').length}
            </p>
          </div>

          <div className="glass-surface rounded-xl p-6 border border-white/[0.06]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-muted">Completados</h3>
              <BookOpen className="w-5 h-5 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              {courses.filter((c) => c.status === 'completed').length}
            </p>
          </div>
        </div>
      )}

      {attendanceModalOpen && selectedCourseForAttendance && (
        <AttendanceModal
          isOpen={attendanceModalOpen}
          onClose={() => {
            setAttendanceModalOpen(false);
            setSelectedCourseForAttendance(null);
          }}
          courseId={selectedCourseForAttendance.id}
          courseName={selectedCourseForAttendance.name}
        />
      )}

      {evaluationsModalOpen && selectedCourseForAttendance && (
        <EvaluationsModal
          isOpen={evaluationsModalOpen}
          onClose={() => {
            setEvaluationsModalOpen(false);
            setSelectedCourseForAttendance(null);
          }}
          courseId={selectedCourseForAttendance.id}
          courseName={selectedCourseForAttendance.name}
        />
      )}

      <ProtocoloBimModal
        open={protocoloCourseId !== null}
        onClose={() => setProtocoloCourseId(null)}
        courseId={protocoloCourseId}
      />
    </div>
  );
}
